import { useState } from "react";
import { Copy, Check } from "lucide-react";

// Parse and render markdown content with proper styling
export function MarkdownContent({ content }) {
  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  let inCodeBlock = false;
  let codeBlockLines = [];
  let codeLanguage = '';

  while (i < lines.length) {
    const line = lines[i];

    // Code block handling
    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeLanguage = line.substring(3).trim();
        codeBlockLines = [];
      } else {
        // End of code block
        elements.push(
          <CodeBlock 
            key={`code-${i}`} 
            code={codeBlockLines.join('\n')} 
            language={codeLanguage}
          />
        );
        inCodeBlock = false;
        codeBlockLines = [];
        codeLanguage = '';
      }
      i++;
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      i++;
      continue;
    }

    // Table handling
    if (line.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      elements.push(<Table key={`table-${i}`} lines={tableLines} />);
      continue;
    }

    // Headers
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={i} className="text-3xl font-bold text-foreground mt-8 mb-4 font-sans">
          {line.substring(2)}
        </h1>
      );
      i++;
      continue;
    }

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-2xl font-bold text-foreground mt-8 mb-4 font-sans border-b border-white/10 pb-2">
          {line.substring(3)}
        </h2>
      );
      i++;
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-xl font-semibold text-foreground mt-6 mb-3 font-sans">
          {line.substring(4)}
        </h3>
      );
      i++;
      continue;
    }

    // Lists
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const listItems = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        listItems.push(lines[i].substring(2));
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} className="my-4 space-y-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-primary mt-1.5">•</span>
              <span className="text-foreground/90">{formatInlineText(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered lists
    if (/^\d+\.\s/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={`olist-${i}`} className="my-4 space-y-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="text-primary font-bold min-w-[20px]">{idx + 1}.</span>
              <span className="text-foreground/90">{formatInlineText(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Status indicators
    if (line.startsWith('✅') || line.startsWith('❌') || line.startsWith('⚠️') || line.startsWith('💡')) {
      elements.push(
        <div key={i} className="my-2 flex items-start gap-2">
          <span className="text-lg">{line.charAt(0)}{line.charAt(1)}</span>
          <span className="text-foreground/90">{formatInlineText(line.substring(2).trim())}</span>
        </div>
      );
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={i} className="my-3 text-foreground/90 leading-relaxed">
        {formatInlineText(line)}
      </p>
    );
    i++;
  }

  return <div className="space-y-1">{elements}</div>;
}

// Format inline text (bold, italic, code)
function formatInlineText(text) {
  if (!text) return text;
  
  const parts = [];
  let remaining = text;
  let keyIndex = 0;

  // Process inline code first
  while (remaining.includes('`')) {
    const startIdx = remaining.indexOf('`');
    const endIdx = remaining.indexOf('`', startIdx + 1);
    
    if (endIdx === -1) break;
    
    if (startIdx > 0) {
      parts.push(formatBoldItalic(remaining.substring(0, startIdx), keyIndex++));
    }
    
    parts.push(
      <code key={`code-${keyIndex++}`} className="px-1.5 py-0.5 bg-black/40 rounded text-primary text-sm font-mono">
        {remaining.substring(startIdx + 1, endIdx)}
      </code>
    );
    
    remaining = remaining.substring(endIdx + 1);
  }
  
  if (remaining) {
    parts.push(formatBoldItalic(remaining, keyIndex));
  }
  
  return parts.length > 0 ? parts : text;
}

function formatBoldItalic(text, baseKey) {
  if (!text) return text;
  
  // Handle **bold**
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    parts.push(
      <strong key={`bold-${baseKey}-${keyIndex++}`} className="font-bold text-foreground">
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

// Code block component with copy button
function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-6 rounded-xl overflow-hidden border border-white/10">
      <div className="flex items-center justify-between px-4 py-2 bg-black/60 border-b border-white/10">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {language || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-green-400" />
              <span className="text-green-400">Скопійовано!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Копіювати</span>
            </>
          )}
        </button>
      </div>
      <pre className="p-4 bg-black/40 overflow-x-auto">
        <code className="text-sm font-mono leading-relaxed text-foreground/90">
          {code}
        </code>
      </pre>
    </div>
  );
}

// Table component
function Table({ lines }) {
  if (lines.length < 2) return null;

  // Parse header
  const headerCells = lines[0]
    .split('|')
    .filter(cell => cell.trim() !== '')
    .map(cell => cell.trim());

  // Skip separator line (|---|---|)
  const dataStartIndex = lines[1].includes('---') ? 2 : 1;

  // Parse data rows
  const dataRows = lines.slice(dataStartIndex).map(line =>
    line
      .split('|')
      .filter(cell => cell.trim() !== '')
      .map(cell => cell.trim())
  );

  return (
    <div className="my-6 overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full">
        <thead>
          <tr className="bg-primary/20 border-b border-white/10">
            {headerCells.map((cell, idx) => (
              <th
                key={idx}
                className="px-4 py-3 text-left text-sm font-bold text-foreground"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rowIdx) => (
            <tr
              key={rowIdx}
              className={`border-b border-white/5 ${
                rowIdx % 2 === 0 ? 'bg-white/5' : 'bg-black/20'
              }`}
            >
              {row.map((cell, cellIdx) => (
                <td
                  key={cellIdx}
                  className="px-4 py-3 text-sm text-foreground/90"
                >
                  {formatInlineText(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default MarkdownContent;
